import type { BlogPostResponse, ShipstarConfig } from "./types";
import { escapeHtml } from "./utils";
import { markdownToHtml } from "./markdown";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function isoDate(dateStr: string | null): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toISOString();
  } catch {
    return dateStr;
  }
}

interface TocEntry {
  level: number;
  text: string;
  slug: string;
}

function extractTitle(content: string): { title: string; body: string } {
  const lines = content.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith("# ")) {
      return {
        title: trimmed.slice(2).trim(),
        body: [...lines.slice(0, i), ...lines.slice(i + 1)].join("\n").trim(),
      };
    }
    // Skip empty lines at the start
    if (trimmed !== "") break;
  }
  return { title: "", body: content };
}

function extractExcerpt(body: string, maxLen = 180): string {
  const lines = body.split("\n");
  let inCodeBlock = false;
  for (const line of lines) {
    if (line.trimStart().startsWith("```")) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;
    const trimmed = line.trim();
    // Skip headings, empty lines, horizontal rules, list items starting with bullet
    if (!trimmed || /^#{1,6}\s/.test(trimmed) || /^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) continue;
    // Skip blockquotes
    if (trimmed.startsWith("> ")) continue;
    // Strip markdown formatting
    const plain = trimmed
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/\*(.+?)\*/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
    if (plain.length > 20) {
      return plain.length > maxLen
        ? plain.slice(0, maxLen).replace(/\s+\S*$/, "") + "..."
        : plain;
    }
  }
  return "";
}

function readingTime(content: string): number {
  const words = content.replace(/```[\s\S]*?```/g, "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
}

function extractHeadings(content: string): TocEntry[] {
  const headings: TocEntry[] = [];
  const lines = content.split("\n");
  let inCodeBlock = false;

  for (const line of lines) {
    if (line.trimStart().startsWith("```")) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;

    const match = line.trim().match(/^(#{1,6})\s+(.+)$/);
    if (match) {
      const level = match[1].length;
      const text = match[2].replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").replace(/`([^`]+)`/g, "$1");
      const slug = text
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
      headings.push({ level, text, slug });
    }
  }

  return headings;
}

function renderToc(headings: TocEntry[]): string {
  if (headings.length === 0) return "";

  const minLevel = Math.min(...headings.map((h) => h.level));

  const items = headings
    .map((h) => {
      const indent = h.level - minLevel;
      const paddingClass = indent > 0 ? ` ss-toc-indent-${Math.min(indent, 3)}` : "";
      return `<li class="ss-toc-item${paddingClass}">
        <a href="#${h.slug}" class="ss-toc-link" data-ss-toc-target="${h.slug}">${escapeHtml(h.text)}</a>
      </li>`;
    })
    .join("");

  return `<nav class="ss-toc" aria-label="Table of contents">
    <div class="ss-toc-header">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
      <span>On this page</span>
    </div>
    <ol class="ss-toc-list">${items}</ol>
  </nav>`;
}

function renderShareButtons(): string {
  return `<aside class="ss-share" aria-label="Share this article">
    <span class="ss-share-label">Share</span>
    <div class="ss-share-buttons">
      <button class="ss-share-btn" data-ss-share="twitter" title="Share on Twitter" aria-label="Share on Twitter">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
        </svg>
      </button>
      <button class="ss-share-btn" data-ss-share="linkedin" title="Share on LinkedIn" aria-label="Share on LinkedIn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="currentColor"/>
        </svg>
      </button>
      <button class="ss-share-btn" data-ss-share="copy" title="Copy link" aria-label="Copy link">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </aside>`;
}

const shipstarIcon = `<svg width="14" height="14" viewBox="0 0 195 195" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M123.215 24.3304C125.235 21.8047 129.145 21.8047 128.921 24.3304L124.507 73.9972C124.406 75.1267 125.218 75.8915 126.518 75.8915H183.666C186.572 75.8915 186.27 79.2898 183.225 80.8508L123.349 111.547C121.987 112.245 120.997 113.482 120.897 114.612L116.482 164.278C116.258 166.804 112.161 168.904 110.504 167.343L77.9123 136.648C77.1711 135.949 75.7473 135.949 74.3856 136.648L14.5092 167.343C11.4643 168.904 9.23429 166.804 11.2549 164.278L50.9888 114.612C51.8924 113.482 52.0024 112.245 51.2612 111.547L18.6698 80.8508C17.0125 79.2898 19.7312 75.8915 22.6373 75.8915H79.7856C81.0852 75.8915 82.577 75.1267 83.4806 73.9972L123.215 24.3304Z" fill="currentColor"/></svg>`;

const calendarIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;

const clockIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;

const arrowRightIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>`;

const arrowLeftIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>`;

const tagIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z"/><path d="M7 7h.01"/></svg>`;

const starIcon = `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;

function renderCategoryBadge(category: string | null | undefined, featured = false): string {
  if (featured) {
    const label = category ? `Featured &middot; ${escapeHtml(category)}` : "Featured";
    return `<span class="ss-blog-category ss-blog-category-featured">${starIcon} ${label}</span>`;
  }
  if (!category) return "";
  return `<span class="ss-blog-category">${tagIcon} ${escapeHtml(category)}</span>`;
}

// ---------------------------------------------------------------------------
// Single blog post view (with TOC + share sidebars)
// ---------------------------------------------------------------------------

export function renderBlogWidget(
  data: BlogPostResponse,
  config: ShipstarConfig
): string {
  if (!data.content || data.content.trim() === "") {
    return `<div class="ss-widget ss-blog"><div class="ss-empty">No blog post available.</div></div>`;
  }

  const { title, body } = extractTitle(data.content);
  const headings = extractHeadings(body);

  const dateStr = formatDate(data.published_at || data.created_at);
  const dateIso = isoDate(data.published_at || data.created_at);
  const dateHtml = dateStr
    ? `<time class="ss-blog-date" datetime="${dateIso}">${escapeHtml(dateStr)}</time>`
    : "";

  const categoryHtml = renderCategoryBadge(data.category);

  const titleHtml = title
    ? `<h1 class="ss-blog-title">${escapeHtml(title)}</h1>`
    : "";

  const contentHtml = markdownToHtml(body);

  const hasSidebars = headings.length > 0;

  const tocHtml = hasSidebars ? renderToc(headings) : "";
  const shareHtml = renderShareButtons();

  const footerHtml = config.branding
    ? `<div class="ss-footer">
    <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
  </div>`
    : "";

  // Build structured data for SEO/LLMs
  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title || undefined,
    datePublished: dateIso || undefined,
    articleBody: data.content.replace(/[#*`[\]()>]/g, "").slice(0, 500),
  };

  const jsonLd = `<script type="application/ld+json">${JSON.stringify(structuredData)}</script>`;

  const mins = readingTime(data.content);
  const metaHtml = `<div class="ss-blog-card-meta ss-blog-header-meta">
    ${dateStr ? `<span class="ss-blog-card-meta-item">${calendarIcon} <time datetime="${dateIso}">${escapeHtml(dateStr)}</time></span>` : ""}
    <span class="ss-blog-card-meta-item">${clockIcon} ${mins} min read</span>
  </div>`;

  // Back to blog link (uses onNavigate if available)
  const backHtml = `<div class="ss-blog-detail-nav">
    <button class="ss-blog-back" type="button" data-ss-back-to-blog>
      ${arrowLeftIcon} <span>Back to Blog</span>
    </button>
  </div>`;

  if (hasSidebars) {
    return `${jsonLd}
<div class="ss-widget ss-blog ss-blog-with-nav">
  ${backHtml}
  <div class="ss-blog-layout">
    <div class="ss-blog-header">
      ${categoryHtml}
      ${titleHtml}
      ${metaHtml}
    </div>
    <div class="ss-blog-sidebar ss-blog-sidebar-left">
      ${tocHtml}
    </div>
    <article class="ss-blog-main" role="main">
      <div class="ss-blog-body">${contentHtml}</div>
      ${footerHtml}
    </article>
    <div class="ss-blog-sidebar ss-blog-sidebar-right">
      ${shareHtml}
    </div>
  </div>
</div>`;
  }

  return `${jsonLd}
<div class="ss-widget ss-blog">
  ${backHtml}
  <article role="main">
    <div class="ss-blog-header">
      ${categoryHtml}
      ${titleHtml}
      ${metaHtml}
    </div>
    <div class="ss-blog-body">${contentHtml}</div>
  </article>
  <div class="ss-blog-share-inline">${shareHtml}</div>
  ${footerHtml}
</div>`;
}

// ---------------------------------------------------------------------------
// Blog feed: static render (no navigation) — backwards compat
// ---------------------------------------------------------------------------

export function renderBlogFeedWidget(
  posts: BlogPostResponse[],
  config: ShipstarConfig
): string {
  if (!posts || posts.length === 0) {
    return `<div class="ss-widget ss-blog"><div class="ss-empty">No blog posts available.</div></div>`;
  }

  const items = posts.slice(0, config.maxItems);

  const postsHtml = items
    .map((post) => {
      const { title, body } = extractTitle(post.content);
      const dateStr = formatDate(post.published_at || post.created_at);
      const dateIso = isoDate(post.published_at || post.created_at);
      const dateHtml = dateStr
        ? `<time class="ss-blog-date" datetime="${dateIso}">${escapeHtml(dateStr)}</time>`
        : "";

      const titleHtml = title
        ? `<h2 class="ss-blog-feed-title">${escapeHtml(title)}</h2>`
        : "";

      const contentHtml = markdownToHtml(body);

      return `<article class="ss-blog-post">
        <div class="ss-blog-header">
          ${dateHtml}
          ${titleHtml}
        </div>
        <div class="ss-blog-body">${contentHtml}</div>
      </article>`;
    })
    .join("");

  const footerHtml = config.branding
    ? `<div class="ss-footer">
    <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
  </div>`
    : "";

  return `<div class="ss-widget ss-blog ss-blog-feed">
    ${postsHtml}
    ${footerHtml}
  </div>`;
}

// ---------------------------------------------------------------------------
// Blog feed: interactive card listing with hero + grid (like KB)
// ---------------------------------------------------------------------------

function renderHeroCard(post: BlogPostResponse): string {
  const { title, body } = extractTitle(post.content);
  const excerpt = extractExcerpt(body, 250);
  const dateStr = formatDate(post.published_at || post.created_at);
  const dateIso = isoDate(post.published_at || post.created_at);
  const mins = readingTime(post.content);

  return `<a class="ss-blog-hero-card" href="#" data-blog-slug="${escapeHtml(post.slug)}">
    ${renderCategoryBadge(post.category, true)}
    <h2 class="ss-blog-hero-title">${escapeHtml(title)}</h2>
    ${excerpt ? `<p class="ss-blog-hero-excerpt">${escapeHtml(excerpt)}</p>` : ""}
    <div class="ss-blog-card-meta">
      ${dateStr ? `<span class="ss-blog-card-meta-item">${calendarIcon} <time datetime="${dateIso}">${escapeHtml(dateStr)}</time></span>` : ""}
      <span class="ss-blog-card-meta-item">${clockIcon} ${mins} min read</span>
    </div>
    <span class="ss-blog-hero-cta">Read Article ${arrowRightIcon}</span>
  </a>`;
}

function renderPostCard(post: BlogPostResponse): string {
  const { title, body } = extractTitle(post.content);
  const excerpt = extractExcerpt(body, 140);
  const dateStr = formatDate(post.published_at || post.created_at);
  const dateIso = isoDate(post.published_at || post.created_at);
  const mins = readingTime(post.content);

  return `<a class="ss-blog-card" href="#" data-blog-slug="${escapeHtml(post.slug)}">
    ${renderCategoryBadge(post.category)}
    <h3 class="ss-blog-card-title">${escapeHtml(title)}</h3>
    ${excerpt ? `<p class="ss-blog-card-excerpt">${escapeHtml(excerpt)}</p>` : ""}
    <div class="ss-blog-card-meta">
      ${dateStr ? `<span class="ss-blog-card-meta-item">${calendarIcon} <time datetime="${dateIso}">${escapeHtml(dateStr)}</time></span>` : ""}
      <span class="ss-blog-card-meta-item">${clockIcon} ${mins} min read</span>
    </div>
  </a>`;
}

/**
 * Mounts an interactive blog feed widget with hero card + grid listing.
 * Clicking a card triggers onNavigate(slug) for the host page to handle routing.
 * If no onNavigate, renders the full post inline (detail view).
 */
export function mountBlogFeedWidget(
  shadowRoot: ShadowRoot,
  posts: BlogPostResponse[],
  config: ShipstarConfig
): void {
  if (!posts || posts.length === 0) {
    const empty = document.createElement("div");
    empty.innerHTML = `<div class="ss-widget ss-blog"><div class="ss-empty">No blog posts available.</div></div>`;
    shadowRoot.appendChild(empty);
    return;
  }

  const allPosts = posts.slice(0, config.maxItems);

  const wrapper = document.createElement("div");
  wrapper.className = "ss-widget ss-blog ss-blog-feed";

  // ── List view ──────────────────────────────────────────────────────
  const listView = document.createElement("div");
  listView.className = "ss-blog-view ss-blog-list-view";

  // Hero card (first post)
  const heroEl = document.createElement("div");
  heroEl.className = "ss-blog-hero";
  heroEl.innerHTML = renderHeroCard(allPosts[0]);
  listView.appendChild(heroEl);

  // Grid cards (remaining posts)
  if (allPosts.length > 1) {
    const gridEl = document.createElement("div");
    gridEl.className = "ss-blog-grid";
    gridEl.innerHTML = allPosts.slice(1).map((p) => renderPostCard(p)).join("");
    listView.appendChild(gridEl);
  }

  // Footer (list view)
  if (config.branding) {
    const footer = document.createElement("div");
    footer.innerHTML = `<div class="ss-footer">
      <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
    </div>`;
    listView.appendChild(footer);
  }

  wrapper.appendChild(listView);

  // ── Detail view ────────────────────────────────────────────────────
  const detailView = document.createElement("div");
  detailView.className = "ss-blog-view ss-blog-detail-view ss-blog-hidden";
  wrapper.appendChild(detailView);

  // Detail footer
  const detailFooter = document.createElement("div");
  if (config.branding) {
    detailFooter.innerHTML = `<div class="ss-footer">
      <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
    </div>`;
  }

  shadowRoot.appendChild(wrapper);

  // ── Navigation helpers ─────────────────────────────────────────────

  function navigateToPost(slug: string): void {
    if (config.onNavigate) {
      config.onNavigate(slug);
      return;
    }
    showPost(slug);
  }

  function navigateToList(): void {
    if (config.onNavigate) {
      config.onNavigate("");
      return;
    }
    showList();
  }

  function showList(): void {
    listView.classList.remove("ss-blog-hidden");
    detailView.classList.add("ss-blog-hidden");
    wrapper.scrollTop = 0;
  }

  function showPost(slug: string): void {
    const post = allPosts.find((p) => p.slug === slug);
    if (!post) return;

    const { title, body } = extractTitle(post.content);
    const headings = extractHeadings(body);
    const contentHtml = markdownToHtml(body);
    const dateStr = formatDate(post.published_at || post.created_at);
    const dateIso = isoDate(post.published_at || post.created_at);

    const mins = readingTime(post.content);
    const metaHtml = `<div class="ss-blog-card-meta ss-blog-header-meta">
      ${dateStr ? `<span class="ss-blog-card-meta-item">${calendarIcon} <time datetime="${dateIso}">${escapeHtml(dateStr)}</time></span>` : ""}
      <span class="ss-blog-card-meta-item">${clockIcon} ${mins} min read</span>
    </div>`;

    const tocHtml = headings.length > 0 ? renderToc(headings) : "";
    const shareHtml = renderShareButtons();
    const hasSidebars = headings.length > 0;

    // Find related posts (next/prev + random, max 3)
    const idx = allPosts.indexOf(post);
    const others = allPosts.filter((_, i) => i !== idx);
    const related = others.slice(0, 3);
    const relatedHtml = related.length > 0
      ? `<div class="ss-blog-related">
          <div class="ss-blog-related-divider"></div>
          <h3 class="ss-blog-related-heading">More Articles</h3>
          <div class="ss-blog-related-grid">
            ${related.map((r) => {
              const { title: rTitle } = extractTitle(r.content);
              const rDate = formatDate(r.published_at || r.created_at);
              return `<a class="ss-blog-related-card" href="#" data-blog-slug="${escapeHtml(r.slug)}">
                <h4 class="ss-blog-related-title">${escapeHtml(rTitle)}</h4>
                ${rDate ? `<span class="ss-blog-related-date">${escapeHtml(rDate)}</span>` : ""}
              </a>`;
            }).join("")}
          </div>
        </div>`
      : "";

    const backNav = `<div class="ss-blog-detail-nav">
      <button class="ss-blog-back" type="button">${arrowLeftIcon} <span>Back to Blog</span></button>
    </div>`;

    const categoryHtml = renderCategoryBadge(post.category);
    const headerHtml = `<div class="ss-blog-header">
      ${categoryHtml}
      <h1 class="ss-blog-title">${escapeHtml(title)}</h1>
      ${metaHtml}
    </div>`;

    if (hasSidebars) {
      detailView.innerHTML = `${backNav}
        <div class="ss-blog-with-nav">
          <div class="ss-blog-layout">
            ${headerHtml}
            <div class="ss-blog-sidebar ss-blog-sidebar-left">${tocHtml}</div>
            <article class="ss-blog-main" role="main">
              <div class="ss-blog-body">${contentHtml}</div>
              ${relatedHtml}
            </article>
            <div class="ss-blog-sidebar ss-blog-sidebar-right">${shareHtml}</div>
          </div>
        </div>`;
    } else {
      detailView.innerHTML = `${backNav}
        <article role="main">
          ${headerHtml}
          <div class="ss-blog-body">${contentHtml}</div>
          <div class="ss-blog-share-inline">${shareHtml}</div>
          ${relatedHtml}
        </article>`;
    }

    detailView.appendChild(detailFooter);
    listView.classList.add("ss-blog-hidden");
    detailView.classList.remove("ss-blog-hidden");
    wrapper.scrollTop = 0;

    // Wire up back button
    const backBtn = detailView.querySelector<HTMLButtonElement>(".ss-blog-back");
    if (backBtn) {
      backBtn.addEventListener("click", (e) => {
        e.preventDefault();
        navigateToList();
      });
    }

    // Wire up related card clicks
    detailView.querySelectorAll<HTMLAnchorElement>("[data-blog-slug]").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const targetSlug = link.getAttribute("data-blog-slug");
        if (targetSlug) navigateToPost(targetSlug);
      });
    });

    // Mount interactivity (TOC + share)
    mountBlogInteractivity(shadowRoot);
  }

  // ── Event delegation for card clicks ───────────────────────────────

  wrapper.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest<HTMLAnchorElement>("[data-blog-slug]");
    if (!target || !listView.contains(target)) return;
    e.preventDefault();
    const slug = target.getAttribute("data-blog-slug");
    if (slug) navigateToPost(slug);
  });
}

/**
 * Mount interactive blog features (TOC highlighting, share buttons, back nav).
 * Called after the widget HTML is inserted into the shadow DOM.
 */
export function mountBlogInteractivity(shadowRoot: ShadowRoot, config?: ShipstarConfig): void {
  // Back to blog button (single post widget)
  const backBtn = shadowRoot.querySelector<HTMLButtonElement>("[data-ss-back-to-blog]");
  if (backBtn && config?.onNavigate) {
    backBtn.addEventListener("click", (e) => {
      e.preventDefault();
      config.onNavigate!("");
    });
  }

  // TOC active heading tracking
  const tocLinks = shadowRoot.querySelectorAll<HTMLAnchorElement>(".ss-toc-link");
  if (tocLinks.length > 0) {
    const headingEls: HTMLElement[] = [];
    tocLinks.forEach((link) => {
      const targetId = link.getAttribute("data-ss-toc-target");
      if (targetId) {
        const heading = shadowRoot.querySelector<HTMLElement>(`#${CSS.escape(targetId)}`);
        if (heading) headingEls.push(heading);
      }
    });

    // Click handler for smooth scroll
    tocLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        const targetId = link.getAttribute("data-ss-toc-target");
        if (targetId) {
          const heading = shadowRoot.querySelector<HTMLElement>(`#${CSS.escape(targetId)}`);
          if (heading) {
            heading.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      });
    });

    // Intersection observer for active TOC highlighting
    if (headingEls.length > 0 && typeof IntersectionObserver !== "undefined") {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const id = entry.target.getAttribute("id");
              tocLinks.forEach((link) => {
                link.classList.toggle(
                  "ss-toc-active",
                  link.getAttribute("data-ss-toc-target") === id
                );
              });
            }
          });
        },
        { rootMargin: "-20% 0px -70% 0px" }
      );

      headingEls.forEach((el) => observer.observe(el));
    }
  }

  // Share buttons
  const shareButtons = shadowRoot.querySelectorAll<HTMLButtonElement>(".ss-share-btn");
  shareButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const type = btn.getAttribute("data-ss-share");
      const url = window.location.href;
      const titleEl = shadowRoot.querySelector<HTMLElement>(".ss-blog-title");
      const title = titleEl?.textContent || document.title || "";

      if (type === "twitter") {
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
          "_blank",
          "noopener,noreferrer,width=550,height=420"
        );
      } else if (type === "linkedin") {
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
          "_blank",
          "noopener,noreferrer,width=550,height=420"
        );
      } else if (type === "copy") {
        navigator.clipboard.writeText(url).then(() => {
          const svg = btn.querySelector("svg");
          if (svg) {
            const original = svg.innerHTML;
            svg.innerHTML = `<path d="M5 13l4 4L19 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
            setTimeout(() => {
              svg.innerHTML = original;
            }, 2000);
          }
        });
      }
    });
  });
}
